import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useListProjectsQuery } from '../../api/project';
import { useListImagesQuery } from '../../api/image';
import { RootState } from '../../Store/store';

interface SearchResult {
  id: string;
  name: string;
  type: 'project' | 'image';
  score: number;
  projectId?: string;
}

export const CommandPalette: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { projectId: currentProjectId } = useParams<{ projectId: string }>();
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Get projects from RTK Query
  const { data: projects = [] } = useListProjectsQuery({});
  
  // Get images from RTK Query only if we're in a project
  const { data: imagesData } = useListImagesQuery({ 
    projectId: currentProjectId || '' 
  }, {
    skip: !currentProjectId // Skip the query if we're not in a project
  });
  const images = imagesData?.images || [];

  // Toggle command palette with cmd+k or ctrl+k
  useHotkeys('mod+k', (e: KeyboardEvent) => {
    e.preventDefault();
    setIsOpen(true);
  }, { 
    enableOnFormTags: true,
    preventDefault: true
  });

  // Close on escape
  useHotkeys('esc', () => {
    setIsOpen(false);
  }, { enabled: isOpen });

  // Handle keyboard navigation
  useHotkeys('up', () => {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
  }, { enabled: isOpen });

  useHotkeys('down', () => {
    setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
  }, { enabled: isOpen });

  useHotkeys('enter', () => {
    if (results.length === 1 || selectedIndex >= 0) {
      const selected = results[selectedIndex];
      if (selected.type === 'project') {
        navigate(`/projects/${selected.id}`);
      } else if (selected.type === 'image' && selected.projectId) {
        navigate(`/projects/${selected.projectId}/images/${selected.id}`);
      }
      setIsOpen(false);
    }
  }, { enabled: isOpen });

  // Handle escape key on input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const input = inputRef.current;
    if (input) {
      input.addEventListener('keydown', handleKeyDown);
      return () => input.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      const searchTerms = searchQuery.toLowerCase().split(' ').filter(Boolean);
      const newResults: SearchResult[] = [];

      // Search in projects
      projects.forEach((project: { id: string; name: string }) => {
        const projectName = project.name.toLowerCase();
        const score = searchTerms.reduce((acc, term) => {
          return acc + (projectName.includes(term) ? 1 : 0);
        }, 0);

        if (score > 0) {
          newResults.push({
            id: project.id,
            name: project.name,
            type: 'project',
            score,
          });
        }
      });

      // Search in images only if we're in a project
      if (currentProjectId) {
        images.forEach((image: { id: string; name: string }) => {
          const imageName = image.name.toLowerCase();
          const score = searchTerms.reduce((acc, term) => {
            return acc + (imageName.includes(term) ? 1 : 0);
          }, 0);

          if (score > 0) {
            newResults.push({
              id: image.id,
              name: image.name,
              type: 'image',
              projectId: currentProjectId,
              score,
            });
          }
        });
      }

      // Sort by score (best matches first)
      newResults.sort((a, b) => b.score - a.score);
      setResults(newResults);
    }, 150); // 150ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, projects, images, currentProjectId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-start justify-center pt-20">
      <div ref={modalRef} className="bg-base-100 dark:bg-base-200 rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="p-4">
          <input
            ref={inputRef}
            type="text"
            className="w-full px-4 py-2 text-lg border border-base-300 dark:border-base-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-base-100 dark:bg-base-200 text-base-content"
            placeholder={currentProjectId ? "Search projects and images..." : "Search projects..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-96 overflow-y-auto">
          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}`}
              className={`px-4 py-2 cursor-pointer ${
                index === selectedIndex 
                  ? 'bg-primary/10 dark:bg-primary/20' 
                  : 'hover:bg-base-200 dark:hover:bg-base-300'
              }`}
              onClick={() => {
                setSelectedIndex(index);
                if (result.type === 'project') {
                  navigate(`/projects/${result.id}`);
                } else if (result.type === 'image' && result.projectId) {
                  navigate(`/projects/${result.projectId}/images/${result.id}`);
                }
                setIsOpen(false);
              }}
            >
              <div className="flex items-center">
                <span className="text-sm text-base-content/70 mr-2">
                  {result.type === 'project' ? '📁' : '🖼️'}
                </span>
                <span className="text-base-content">{result.name}</span>
              </div>
            </div>
          ))}
          {results.length === 0 && searchQuery && (
            <div className="px-4 py-2 text-base-content/70">No results found</div>
          )}
        </div>
      </div>
    </div>
  );
}; 